import React, { useState } from "react";
import { Link } from "react-router-dom";

const thirdPartyModules = [
  {
    name: "VirusTotal",
    description:
      "VirusTotal offers a robust analysis tool for files, URLs, IPs, and domains to detect malicious content by aggregating results from many antivirus engines and tools.",
    website: "https://www.virustotal.com/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://www.virustotal.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          VirusTotal
        </a>
        .<br />
        2. Create a free account or log in.<br />
        3. Navigate to your profile → API Key section.<br />
        4. Copy your API key and enter it in Yivo’s API configuration.<br />
        <em>Note: Free tier has rate limits; paid plans offer extended usage.</em>
      </>
    ),
  },
  {
    name: "ThreatFox",
    description:
      "ThreatFox provides community-driven threat intelligence for hashes, URLs, IPs, and domains to help detect malicious activities.",
    website: "https://threatfoxapi.com/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://threatfoxapi.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          ThreatFox
        </a>
        .<br />
        2. Register for an account.<br />
        3. Generate an API key from your user dashboard.<br />
        4. Enter the API key in Yivo.
      </>
    ),
  },
  {
    name: "AlienVault",
    description:
      "AlienVault offers real-time threat intelligence on malicious activity with detailed information about IPs, URLs, and domains.",
    website: "https://otx.alienvault.com/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://otx.alienvault.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          AlienVault OTX
        </a>
        .<br />
        2. Sign up for a free account.<br />
        3. Go to your profile → API Keys to generate your key.<br />
        4. Use the key in Yivo’s configuration.
      </>
    ),
  },
  {
    name: "Shodan",
    description:
      "Shodan is a search engine for Internet-connected devices, offering detailed information about IP addresses and their security status.",
    website: "https://www.shodan.io/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://www.shodan.io/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          Shodan
        </a>
        .<br />
        2. Create a free or paid account.<br />
        3. Find your API key in your dashboard.<br />
        4. Enter the key in Yivo.
      </>
    ),
  },
  {
    name: "APIVoid",
    description:
      "APIVoid provides reputation and security analysis services for domains, IPs, URLs, and hashes.",
    website: "https://www.apivoid.com/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://www.apivoid.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          APIVoid
        </a>
        .<br />
        2. Register an account.<br />
        3. Generate an API key from your dashboard.<br />
        4. Use this key in Yivo’s API configuration.
      </>
    ),
  },
  {
    name: "AbuseIPDB",
    description:
      "AbuseIPDB provides a database of reported malicious IP addresses to help identify abusive actors.",
    website: "https://www.abuseipdb.com/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://www.abuseipdb.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          AbuseIPDB
        </a>
        .<br />
        2. Sign up for a free account.<br />
        3. Generate your API key from the user dashboard.<br />
        4. Add the API key to Yivo configuration.
      </>
    ),
  },
  {
    name: "URLHaus",
    description:
      "URLHaus tracks and shares information about malicious URLs used for malware distribution.",
    website: "https://urlhaus.abuse.ch/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://urlhaus.abuse.ch/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          URLHaus
        </a>
        .<br />
        2. Register for an account to get API access.<br />
        3. Generate an API key from your account page.<br />
        4. Configure Yivo with this key.
      </>
    ),
  },
  {
    name: "URLScan",
    description:
      "URLScan provides detailed security and reputation analysis of URLs by scanning and categorizing them.",
    website: "https://urlscan.io/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://urlscan.io/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          URLScan
        </a>
        .<br />
        2. Create a free account.<br />
        3. Access your API key in your user dashboard.<br />
        4. Use it to enable integration with Yivo.
      </>
    ),
  },
  {
    name: "PhishTank",
    description:
      "PhishTank is a community-driven anti-phishing service that provides verified phishing URLs.",
    website: "https://www.phishtank.com/",
    apiKeyInstructions: (
      <>
        1. Visit{" "}
        <a
          href="https://www.phishtank.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-purple-400"
        >
          PhishTank
        </a>
        .<br />
        2. Register for an account.<br />
        3. Obtain your API key from the account settings.<br />
        4. Configure Yivo with the API key.
      </>
    ),
  },
];

const HelpPage = () => {
  // State to track open sections (by name)
  const [openSections, setOpenSections] = useState({
    account: false,
    features: false,
    search: false,
    discover: false,
    share: false,
  });

  // Track which third party modules are expanded
  const [openModules, setOpenModules] = useState({});

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleModule = (moduleName) => {
    setOpenModules((prev) => ({ ...prev, [moduleName]: !prev[moduleName] }));
  };

  return (
    <div className="bg-gray-900 text-white px-4 py-10 max-w-4xl mx-auto">
      <h1 className="text-3xl sm:text-4xl font-bold mb-6">Getting Started with Yivo</h1>

      {/* Account Creation */}
      <section className="mb-10 border-b border-gray-700 pb-6">
        <button
          onClick={() => toggleSection("account")}
          className="w-full flex justify-between items-center text-left focus:outline-none"
          aria-expanded={openSections.account}
          aria-controls="account-section"
        >
          <h2 className="text-2xl font-semibold">1. Account Creation</h2>
          <span className="text-3xl select-none">{openSections.account ? "−" : "+"}</span>
        </button>
        {openSections.account && (
          <p
            id="account-section"
            className="text-gray-300 mt-4 text-sm sm:text-base max-w-prose"
          >
            To begin using Yivo, you'll first need to create an account. Navigate to the Sign Up page,
            provide a valid email and secure password or sign up with your Google account. Then you can log in and start using the platform.
          </p>
        )}
      </section>

      {/* Platform Features */}
      <section className="mb-10 border-b border-gray-700 pb-6">
        <button
          onClick={() => toggleSection("features")}
          className="w-full flex justify-between items-center text-left focus:outline-none"
          aria-expanded={openSections.features}
          aria-controls="features-section"
        >
          <h2 className="text-2xl font-semibold">2. Platform Features</h2>
          <span className="text-3xl select-none">{openSections.features ? "−" : "+"}</span>
        </button>
        {openSections.features && (
          <>
{/* Search */}
<div className="mt-6 border border-gray-700 rounded-md p-4 bg-gray-800">
  <button
    onClick={() => toggleSection("search")}
    className="w-full flex justify-between items-center text-left focus:outline-none"
    aria-expanded={openSections.search}
    aria-controls="search-section"
  >
    <h3 className="text-xl font-semibold">Search</h3>
    <span className="text-2xl select-none">{openSections.search ? "−" : "+"}</span>
  </button>

  {openSections.search && (
    <div id="search-section" className="mt-3 text-gray-300 text-sm sm:text-base">
      <p>
        The Search module allows you to investigate IOCs (Indicators of Compromise) using
        built-in and third-party intelligence feeds.
      </p>
      <ul className="list-disc list-inside mt-2 mb-4 text-gray-400">
        <li>
          Built-in feeds include Onionoo, IPWhois, OpenPhish, Google Safe Browse,
          LocalDB, and MalwareBazaar.
        </li>
        <li>
          Third-party modules such as VirusTotal, ThreatFox, AlienVault, IBM X-Force,
          Shodan, APIVoid, AbuseIPDB, URLHaus, URLScan, and PhishTank can be enabled via
          API key configuration.
        </li>
        <li>
          You can set a <strong>weight</strong> for each feed to prioritize results.
        </li>
        <li>
          Yivo also collects indicators from your configured <strong>MISP</strong> feed
          sources daily and ingests/updates the local database for correlation to enrich
          search results.
        </li>
      </ul>

      {/* Third-party API modules */}
      <div>
        <h4 className="font-semibold text-lg mb-2">Third-Party API Modules & API Key Setup</h4>
        {thirdPartyModules.map(({ name, description, apiKeyInstructions }) => (
          <div
            key={name}
            className="mb-4 border border-gray-700 rounded-md bg-gray-900 p-3"
          >
            <button
              onClick={() => toggleModule(name)}
              className="w-full flex justify-between items-center text-left focus:outline-none"
              aria-expanded={!!openModules[name]}
              aria-controls={`${name}-details`}
            >
              <span className="font-semibold text-purple-400">{name}</span>
              <span className="text-xl select-none">{openModules[name] ? "−" : "+"}</span>
            </button>
            {openModules[name] && (
              <div
                id={`${name}-details`}
                className="mt-2 text-gray-300 text-sm sm:text-base max-w-prose"
              >
                <p className="mb-1">{description}</p>
                <div>{apiKeyInstructions}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Risk Score Model (Experimental) */}
      <div className="mt-6 border-t border-gray-700 pt-4 w-full">
        <h4 className="font-semibold text-lg mb-2 text-purple-300">
          About the Risk Score Model (Experimental)
        </h4>
        <p className="text-gray-300 text-sm sm:text-base w-full">
          Yivo includes an experimental <strong>risk scoring model</strong> to help prioritize indicators based on their
          potential threat level. This model aggregates and normalizes results across multiple intelligence sources,
          factoring in the reputation, frequency, blacklist presence, and historical abuse reports of a given indicator.
        </p>
        <p className="mt-2 text-gray-300 text-sm sm:text-base w-full">
          Each indicator is assigned a <strong>score from 0 to 100</strong>, where higher values indicate greater
          potential risk. Color gradients help quickly visualize severity. However, this score is <em>not a definitive
          judgment</em> — all IOCs should be reviewed within the full context of your investigation.
        </p>
        <p className="mt-2 text-yellow-400 text-sm sm:text-base w-full">
          ⚠️ <strong>Important:</strong> The risk score is <em>experimental</em> and may not always reflect the real-world
          severity of an IOC. Use it as a heuristic, not a final verdict. Always validate indicators with manual
          analysis and multiple sources before taking action.
        </p>
      </div>
    </div>
  )}
</div>


 {/* Discover */}
<div className="mt-6 border border-gray-700 rounded-md p-4 bg-gray-800">
  <button
    onClick={() => toggleSection("discover")}
    className="w-full flex justify-between items-center text-left focus:outline-none"
    aria-expanded={openSections.discover}
    aria-controls="discover-section"
  >
    <h3 className="text-xl font-semibold">Discover</h3>
    <span className="text-2xl select-none">{openSections.discover ? "−" : "+"}</span>
  </button>
  {openSections.discover && (
    <div id="discover-section" className="mt-3 text-gray-300 text-sm sm:text-base space-y-6">

      {/* News Intelligence Feeds */}
      <section>
        <h4 className="text-lg font-semibold text-purple-300">News Intelligence Feeds</h4>
        <p>Stay current with cybersecurity news and threat intelligence from external sources.</p>

        <ul className="list-disc list-inside text-gray-400 space-y-1">
          <li>Enable preconfigured RSS feeds from known threat intel sources.</li>
          <li>Add your own custom RSS feeds by pasting a valid feed URL.</li>
        </ul>

        <h5 className="font-semibold mt-3">Built-in Feed Options</h5>
        <ul className="list-disc list-inside text-gray-300 space-y-1">
          <li><strong>ThreatPost Security News</strong> – Breaking cybersecurity news, trends, and analysis.</li>
          <li><strong>BleepingComputer</strong> – Updates on malware, ransomware, and cybersecurity developments.</li>
          <li><strong>Krebs on Security</strong> – Investigative reporting on cybercrime.</li>
          <li><strong>The Hacker News</strong> – Coverage of hacks, breaches, and vulnerabilities.</li>
          <li><strong>Dark Reading</strong> – Enterprise-level security research and analysis.</li>
          <li><strong>Schneier on Security</strong> – Blog and commentary from Bruce Schneier.</li>
          <li><strong>Security Weekly</strong> – Security podcasts and discussions.</li>
          <li><strong>CISA Alerts</strong> – U.S. government alerts and recommendations.</li>
          <li><strong>US-CERT Vulnerability Notes</strong> – Disclosures and CVE insights.</li>
        </ul>
      </section>

      {/* Scout */}
      <section>
        <h4 className="text-lg font-semibold text-purple-300">Scout (Search)</h4>
        <p>
          <strong>Scout</strong> is Yivo’s on-demand IOC search tool. It allows you to search for
          indicators across internal and third-party sources using keyword-based matching.
        </p>

        <ul className="list-disc list-inside text-gray-300 space-y-1">
          <li>Search the local database and enabled modules like OpenPhish and PhishTank.</li>
          <li>Use <strong>keywords</strong> to filter results from high-volume data sources.</li>
          <li>Scout respects feed weights and normalizes results to assist in prioritization.</li>
        </ul>

        <h5 className="font-semibold mt-3">Keyword Fuzzing</h5>
        <p>
          To increase coverage, Yivo optionally applies <strong>simple fuzzing mutations</strong> to your
          keywords during search. This technique alters your search term (e.g., `apple-pay` → `applepay`, `app1e`, `appl3`)
          to catch variations used in malicious indicators — especially typosquats and evasion techniques.
        </p>
      </section>

      {/* Scout Monitor */}
      <section>
        <h4 className="text-lg font-semibold text-purple-300">Scout Monitor</h4>
        <p>
          <strong>Scout Monitor</strong> is a passive background engine that automatically watches for
          new IOCs that match your keywords. You can configure keywords in the <strong>Settings</strong> page.
        </p>

        <ul className="list-disc list-inside text-gray-300 space-y-1">
          <li>Monitors IOCs ingested into Yivo from feeds like MISP or uploaded files.</li>
          <li>Watches <strong>Certstream</strong> in real time — a feed of newly issued SSL/TLS certificates.</li>
          <li>Alerts you when new indicators or certificate names match your keywords.</li>
          <li>Uses the same fuzzing approach as Scout to widen detection scope.</li>
        </ul>

        <h5 className="font-semibold mt-3">What is Certstream?</h5>
        <p>
          <strong>Certstream</strong> is a live stream of SSL certificates issued globally. Because threat actors
          often register domains just before launching phishing campaigns, monitoring certificate issuance
          is a powerful way to catch malicious infrastructure early.
        </p>
      </section>
    </div>
  )}
</div>
{/* Insights Help */}
<div className="mt-6 border border-gray-700 rounded-md p-4 bg-gray-800">
  <button
    onClick={() => toggleSection("insights")}
    className="w-full flex justify-between items-center text-left focus:outline-none"
    aria-expanded={openSections.insights}
    aria-controls="insights-section"
  >
    <h3 className="text-xl font-semibold">Insights</h3>
    <span className="text-2xl select-none">{openSections.insights ? "−" : "+"}</span>
  </button>
  {openSections.insights && (
    <div id="insights-section" className="mt-3 text-gray-300 text-sm sm:text-base space-y-6">

      {/* Threat Actor Tracking */}
      <section>
        <h4 className="text-lg font-semibold text-purple-300">Threat Actor Tracking</h4>
        <p>
          The <strong>Insights</strong> module allows you to visualize technique usage across tracked threat actors.
          All actor technique mappings are imported from the official MITRE <strong>Enterprise ATT&CK</strong> dataset,
          specifically the JSON files available at:
        </p>
        <a
          href="https://github.com/mitre/cti/tree/master/enterprise-attack"
          className="text-blue-400 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://github.com/mitre/cti/tree/master/enterprise-attack
        </a>
        <p className="mt-2">
          Only the Enterprise ATT&CK domain is currently supported. Additional ATT&CK domains
          (e.g., Mobile, ICS) may be integrated in future releases.
        </p>
        <ul className="list-disc list-inside text-gray-300 space-y-1 mt-2">
          <li>Tracked actors are parsed directly from MITRE-provided STIX files.</li>
          <li>Each actor is mapped to the techniques they are known to use.</li>
          <li>Filter the heatmap by actor to inspect specific adversary tradecraft.</li>
        </ul>
      </section>

      {/* Heatmap Visualization */}
      <section>
        <h4 className="text-lg font-semibold text-purple-300">Techniques Heatmap</h4>
        <p>
          The <strong>Techniques Heatmap</strong> presents a visual matrix of ATT&CK techniques,
          organized by MITRE’s tactic categories. Each cell represents a technique and is
          color-coded based on how frequently it appears across tracked actors.
        </p>

        <h5 className="font-semibold mt-3">Customization</h5>
        <ul className="list-disc list-inside text-gray-300 space-y-1">
          <li><strong>Zoom:</strong> Adjust the grid size for more detail or compactness.</li>
          <li><strong>Group by Parent:</strong> Collapse sub-techniques under their parent category.</li>
          <li><strong>Show Counts:</strong> Toggle visibility of technique usage counts in each cell.</li>
          <li><strong>Show Top Techniques:</strong> Highlight the most frequently used techniques.</li>
          <li><strong>Filter by Actor:</strong> Narrow the view to techniques used by a selected threat actor.</li>
          <li><strong>Color Scale:</strong> Customize the low/high color spectrum for heat intensity.</li>
        </ul>

        <h5 className="font-semibold mt-3">Editing & Persistence</h5>
        <ul className="list-disc list-inside text-gray-300 space-y-1">
          <li><strong>Edit Mode:</strong> Manually change technique counts or remove entries.</li>
          <li><strong>Save Heatmap:</strong> Save a custom heatmap view with a unique name.</li>
          <li><strong>Load Heatmap:</strong> Load any previously saved heatmaps or reset to live actor data.</li>
        </ul>

        <h5 className="font-semibold mt-3">Import/Export</h5>
        <ul className="list-disc list-inside text-gray-300 space-y-1">
          <li><strong>Import MITRE Layers:</strong> Upload a valid ATT&CK Navigator JSON layer file to add new data.</li>
          <li><strong>Export CSV:</strong> Download the current heatmap table for spreadsheet use.</li>
          <li><strong>Export JSON:</strong> Save the underlying data model to re-import or archive.</li>
          <li><strong>Export SVG:</strong> Capture a high-resolution, scalable image of the heatmap view.</li>
        </ul>
      </section>
    </div>
  )}
</div>

            {/* Share */}
            <div className="mt-6 border border-gray-700 rounded-md p-4 bg-gray-800">
              <button
                onClick={() => toggleSection("share")}
                className="w-full flex justify-between items-center text-left focus:outline-none"
                aria-expanded={openSections.share}
                aria-controls="share-section"
              >
                <h3 className="text-xl font-semibold">Share</h3>
                <span className="text-2xl select-none">{openSections.share ? "−" : "+"}</span>
              </button>
              {openSections.share && (
                <div id="share-section" className="mt-3 text-gray-300 text-sm sm:text-base">
                  <p>
                    Yivo lets you contribute back to your team or community by tagging and sharing
                    insights directly within the platform.
                  </p>
                  <ul className="list-disc list-inside mt-2 text-gray-400">
                    <li>Annotate indicators with comments, categories, or custom tags.</li>
                    <li>Mark data for internal visibility or share publicly depending on your permissions.</li>
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Navigation Button */}
      <div className="mt-12 text-center">
        <Link
          to="/search"
          className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-6 rounded"
        >
          Start Searching
        </Link>
      </div>
    </div>
  );
};

export default HelpPage;
